import React, { useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { userSelector } from '../../../reducers/current-user-selectors';
import { loadTwilioErrorLogs } from '../../../twilio-error-log/actions/load-twilio-error-logs';
import {
  twilioErrorLogForMessageSidSelector,
  twilioErrorLogsStateForMessageSidSelector,
} from '../../../twilio-error-log/twilio-error-log-selectors';

import { UiDialog } from '../../../ui/ui-dialog';

import { getDetailedTwilioError } from './descriptive-twilio-errors';

type Props = {
  messageSid: string;
  onClose: () => void;
};

export const TwilioErrorDetailDialog = ({ messageSid, onClose }: Props) => {
  const dispatch = useDispatch();
  const { isAdmin } = useSelector(userSelector);
  const twilioErrorLogs = useSelector((state: any) =>
    twilioErrorLogForMessageSidSelector(state, messageSid),
  );
  const twilioErrorLogsState = useSelector((state: any) =>
    twilioErrorLogsStateForMessageSidSelector(state, messageSid),
  );

  useEffect(() => {
    dispatch(loadTwilioErrorLogs(messageSid));
  });

  if (twilioErrorLogsState?.loading) {
    return null;
  }

  // If we don't find any errors, then we'll use the default error in getDetailedTwilioError
  let errorCode = '';
  let toNumber;
  if (twilioErrorLogs.length) {
    ({ errorCode, toNumber } = twilioErrorLogs.reduce((prev, curr) =>
      curr.errorId > prev.errorId ? curr : prev,
    ));
  }

  const { description, twilioErrorPageLink, solutions } = getDetailedTwilioError(errorCode, {
    phoneNumber: toNumber,
  });

  return (
    <UiDialog size="xs" onClose={onClose} title="Message Not Delivered">
      <UiDialog.Content>
        <div>
          <div>{description}</div>
          <div>
            <div className="typo-label u-margin-top">Troubleshooting Steps</div>
            <ol>
              {solutions.map((s, i) => (
                // This is an array of components so we have nothing but the index to use for the key
                // eslint-disable-next-line react/no-array-index-key
                <li key={`solution-${i}`}>{s}</li>
              ))}
            </ol>
          </div>
        </div>
        {isAdmin && (
          <div className="u-margin-top-xl">
            <div className="typo-label">Admin Area</div>
            <ul>
              <li>Error Code: {errorCode || 'Unknown'}</li>
              <li>
                <a
                  className="typo-link"
                  href={twilioErrorPageLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View Twilio Error Documentation
                </a>
              </li>
              <li>Contact Twilio support with three recent message sids</li>
              <li>Message Sid: {messageSid}</li>
            </ul>
          </div>
        )}
      </UiDialog.Content>
    </UiDialog>
  );
};
